import { useEffect, useRef, useState } from 'react';

/**
 * Hotové rozvozy v teple — pro „K odvozu" v Moje směně (Cockpit 2.0): celé
 * karty objednávek, co se ještě vaří, limit dávky a doporučená jízda.
 *
 * Ptá se jen rozvozce bez dávky (`enabled`). Server odpoví `na_smene: false`
 * každému, kdo není rozvozce se zahájenou směnou, a proužek se pak neukáže.
 *
 * Obnovuje se hned, stejně jako V teple v cockpitu
 * (`RozvozCockpit/hooks/useVTeple`): kuchyň při každé změně vysílá
 * StavKuchyneZmenen (kanál posílá server), pokladna nové a změněné objednávky
 * na objednavky.live. Víc změn naráz se slije do jednoho dotazu (300 ms).
 * Polling po 30 s zůstává jako pojistka pro výpadek WebSocketu; po návratu
 * z pozadí (`app-resumed`) a při focusu se načte znovu.
 */
export default function useVTeple({ enabled = true } = {}) {
    const [stav, setStav] = useState(PRAZDNO);
    const [kanalKuchyne, setKanalKuchyne] = useState(null);
    // Odložené načtení z aktuálního efektu — listener Reverbu ho volá přes ref,
    // aby se kvůli přihlášení kanálu nemusel restartovat polling.
    const obnovitRef = useRef(() => {});

    useEffect(() => {
        if (!enabled) {
            setStav(PRAZDNO);
            return undefined;
        }
        let platne = true;
        let odklad;
        const nacti = () => fetch('/api/rozvoz/v-teple', { credentials: 'same-origin' })
            .then(r => (r.ok ? r.json() : null))
            .then(d => { if (!platne || !d) return;
                const hotove = d.hotove ?? [];
                // Jak dlouho leží ta nejstarší — karta tím říká, jak moc spěchá.
                const casy = hotove.map(o => o.hotovo_v && new Date(o.hotovo_v).getTime()).filter(Boolean);
                const nejdeleMin = casy.length ? Math.max(0, Math.round((Date.now() - Math.min(...casy)) / 60000)) : null;
                setStav({
                    naSmene: !!d.na_smene, pocet: hotove.length, nejdeleMin,
                    hotove, variSe: d.vari_se ?? [], doporuceno: d.doporuceno ?? [], limit: d.limit ?? 4,
                });
                setKanalKuchyne(d.kanal ?? null);
            })
            .catch(() => {});
        nacti();
        const t = setInterval(nacti, 30000);
        const brzy = () => { clearTimeout(odklad); odklad = setTimeout(nacti, 300); };
        window.addEventListener('focus', nacti);
        window.addEventListener('app-resumed', brzy);
        obnovitRef.current = brzy;
        return () => {
            platne = false;
            clearTimeout(odklad);
            clearInterval(t);
            window.removeEventListener('focus', nacti);
            window.removeEventListener('app-resumed', brzy);
            obnovitRef.current = () => {};
        };
    }, [enabled]);

    useEffect(() => {
        if (!enabled || !kanalKuchyne) return undefined;
        let kuchyne = null;
        let objednavky = null;
        const signal = () => obnovitRef.current();

        // `window.Echo` zakládá app.jsx až ve svém efektu — chvíli se na něj čeká.
        const pripoj = () => {
            if (!window.Echo) return false;
            kuchyne = window.Echo.private(kanalKuchyne);
            objednavky = window.Echo.private('objednavky.live');
            kuchyne.listen('.StavKuchyneZmenen', signal);
            objednavky.listen('.ObjednavkaAktualizovana', signal).listen('.NovaObjednavka', signal);
            return true;
        };
        const iv = pripoj() ? null : setInterval(() => { if (pripoj()) clearInterval(iv); }, 300);

        // stopListening, ne leave — kanály sdílí i jiné části OS
        return () => {
            if (iv) clearInterval(iv);
            kuchyne?.stopListening('.StavKuchyneZmenen', signal);
            objednavky?.stopListening('.ObjednavkaAktualizovana', signal).stopListening('.NovaObjednavka', signal);
        };
    }, [enabled, kanalKuchyne]);

    // Po „Vzít" se má seznam obnovit hned, ne až s dalším pollingem.
    return { ...stav, obnovit: () => obnovitRef.current() };
}

const PRAZDNO = { naSmene: false, pocet: 0, nejdeleMin: null, hotove: [], variSe: [], doporuceno: [], limit: 4 };
